import React, { useState } from 'react';
import { SurveyConfig, UserResponse, SurveyStep } from '../types/survey';
import rawConfig from '../config/surveyConfig_2.json';
import { BsSquare, BsCheckSquare } from 'react-icons/bs';
import * as filestack from 'filestack-js';
import { submitToAirtable } from '../services/airtable.ts';
import { 
  trackFormSubmission, 
  trackSurveyStart, 
  trackStepComplete, 
  trackPhotoUpload,
  trackMetalSelection,
  trackWeightEntry 
} from '../services/analytics.ts';

// Add type declaration for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

interface SurveyProps {
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  history: number[];
  setHistory: React.Dispatch<React.SetStateAction<number[]>>;
  setIsCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  responses: Record<string, any>;
  setResponses: (responses: Record<string, any>) => void;
  QRCodeComponent: React.FC;
  onComplete: (responses: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    smsOptIn: "Yes" | "No";
    photos: string[];
    details: string;
  }) => void;
}

interface SurveyStepProps {
  step: SurveyStep;
  responses: Record<string, any>;
  setResponses: (responses: Record<string, any>) => void;
  handleNext: () => void;
  handleResponse: (value: any) => void;
  handleNumberInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleTextInput: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  emailError: string;
  setEmailError: React.Dispatch<React.SetStateAction<string>>;
  nameInputRef: React.RefObject<HTMLInputElement | null>;
  weightInputRef: React.RefObject<HTMLInputElement | null>;
  isNextDisabled: () => boolean;
  currentStep: number;
  config: SurveyConfig;
  QRCodeComponent: React.FC;
  validateEmail: (email: string) => boolean;
  validatePhone: (phone: string) => boolean;
  showContactErrors: boolean;
  setShowContactErrors: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCompletedLocal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsCompleted: React.Dispatch<React.SetStateAction<boolean>>;
  onComplete: (responses: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    smsOptIn: "Yes" | "No";
    photos: string[];
    details: string;
  }) => void;
  submissionStatus: {
    success: boolean;
    message: string;
  } | null;
  setSubmissionStatus: React.Dispatch<React.SetStateAction<{
    success: boolean;
    message: string;
  } | null>>;
  weightUnit: 'grams' | 'ounces' | 'pennyweight';
  handleUnitChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const SurveyStepComponent: React.FC<SurveyStepProps> = ({
  step,
  responses,
  setResponses,
  handleNext,
  handleResponse,
  handleNumberInput,
  handleTextInput,
  error,
  setError,
  emailError,
  setEmailError,
  nameInputRef,
  weightInputRef,
  isNextDisabled,
  currentStep,
  config,
  QRCodeComponent,
  validateEmail,
  validatePhone,
  showContactErrors,
  setShowContactErrors,
  setIsCompletedLocal,
  setIsCompleted,
  onComplete,
  submissionStatus,
  setSubmissionStatus,
  weightUnit,
  handleUnitChange,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [activeReviewIndex, setActiveReviewIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    setIsSwiping(true);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      handleReviewNav('next');
    } else if (isRightSwipe) {
      handleReviewNav('prev');
    }

    setIsSwiping(false);
    setTouchStart(null);
    setTouchEnd(null);
  };

  const reviews = [
    {
      text: "Working with Abe Mor was a great experience. They understood sellers' anxiety about selling valuable items and made sure the whole transaction went smoothly and transparently. Thank you!",
      reviewer: "Abhi D.",
      date: "Verified Customer • 3 months ago"
    },
    {
      text: "Selling to Abe Mor was extremely simple and stress free. Abe Mor offers a competitive price. Everyone I spoke with in the process was very friendly, kind, accommodating, and transparent.",
      reviewer: "Kate A.",
      date: "Verified Customer • 1 month ago"
    },
    {
      text: "This by far was one of the easiest experiences I’ve had with a company. The process of selling is super simple. Communication is thorough and efficient. And everything was done within a week.",
      reviewer: "Maria N.",
      date: "Verified Customer • 2 months ago"
    },
    {
      text: "I recently had an outstanding experience selling to Abe Mor. I was thrilled with the fair price I received. I highly recommend Abe Mor for their exceptional service and honest pricing.",
      reviewer: "Dana B.",
      date: "Verified Customer • 3 months ago"
    },
    {
      text: "The process of selling some of my gold was incredibly smooth and quick. I knew I didn't want to settle for low-ball offers from any jewelers or pawns. Thank you!",
      reviewer: "Evan A.",
      date: "Verified Customer • 4 months ago"
    },
    {
      text: "I was really nervous selling a gold coin online and mailing it out, but they made it very easy; totally professional, efficient and trustworthy.",
      reviewer: "Chris L.",
      date: "Verified Customer • 5 months ago"
    }
  ];

  const getTitle = () => {
    if (!step.title.includes('{metal}') && !step.title.includes('{intent}')) return step.title;
    
    let title = step.title;
    
    if (step.title.includes('{metal}')) {
      const selectedMetals = responses['metal-type'] as string[] || [];
      if (selectedMetals.length > 0) {
        // For form and purity questions, always use lowercase
        if (step.id.includes('-form') || step.id.includes('-purity')) {
          const formattedMetals = selectedMetals.map(metal => 
            metal.toLowerCase()
          );
          if (formattedMetals.length > 1) {
            const lastMetal = formattedMetals.pop();
            title = title.replace('{metal}', `${formattedMetals.join(', ')}, and ${lastMetal}.`);
          } else {
            title = title.replace('{metal}', `${formattedMetals[0]}.`);
          }
        } else {
          // For other questions, capitalize only if at start of sentence
          const formattedMetals = selectedMetals.map(metal => 
            title.startsWith('{metal}') 
              ? metal.charAt(0).toUpperCase() + metal.slice(1)
              : metal.toLowerCase()
          );
          if (formattedMetals.length > 1) {
            const lastMetal = formattedMetals.pop();
            title = title.replace('{metal}', `${formattedMetals.join(', ')}, and ${lastMetal}.`);
          } else {
            title = title.replace('{metal}', `${formattedMetals[0]}.`);
          }
        }
      } else {
        title = title.replace('{metal}', 'metal.');
      }
    }

    if (step.title.includes('{intent}')) {
      const intent = responses['intent'] === 'Sell' ? 'offer' : 'evaluation';
      title = title.replace('{intent}', intent);
    }

    return title;
  };

  const autoResize = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const handleFileUploadSuccess = (result: any) => {
    setIsUploading(false);
    const uploadedUrls = result.filesUploaded.map((file: any) => file.url);
    setResponses({
      ...responses,
      [step.id]: [...(responses[step.id] || []), ...uploadedUrls]
    });
    trackPhotoUpload(uploadedUrls.length);
  };

  const handleFileUploadError = (error: any) => {
    setIsUploading(false);
    setError('Failed to upload files. Please try again.');
    console.error('File upload error:', error);
  };

  const validateContactFields = () => {
    if (!step.fields) return false;
    
    const contactInfo = responses[step.id] as any;
    let isValid = true;
    
    // Check if fields have any input
    if (step.fields.includes('firstName') && (!contactInfo?.firstName || contactInfo.firstName.trim() === '')) {
      isValid = false;
    }
    
    if (step.fields.includes('lastName') && (!contactInfo?.lastName || contactInfo.lastName.trim() === '')) {
      isValid = false;
    }
    
    if (step.fields.includes('email')) {
      if (!contactInfo?.email || contactInfo.email.trim() === '') {
        isValid = false;
      } else if (!validateEmail(contactInfo.email)) {
        setEmailError('Please enter a valid email address');
        isValid = false;
      } else {
        setEmailError('');
      }
    }
    
    if (step.fields.includes('phone')) {
      if (!contactInfo?.phone || contactInfo.phone.trim() === '') {
        isValid = false;
      } else if (!validatePhone(contactInfo.phone)) {
        setError('Please enter a valid 10-digit phone number');
        isValid = false;
      } else {
        setError('');
      }
    }
    
    return isValid;
  };

  const hasAnyInput = () => {
    if (!step.fields) return false;
    
    const contactInfo = responses[step.id] as any;
    
    if (step.fields.includes('firstName') && contactInfo?.firstName?.trim()) return true;
    if (step.fields.includes('lastName') && contactInfo?.lastName?.trim()) return true;
    if (step.fields.includes('email') && contactInfo?.email?.trim()) return true;
    if (step.fields.includes('phone') && contactInfo?.phone?.trim()) return true;
    
    return false;
  };

  const handleContactNext = async () => {
    if (validateContactFields()) {
      setShowContactErrors(false);
      if (currentStep === config.steps.length - 1) {
        try {
          // Track the final step completion before submission
          trackStepComplete(step.id, step.title, responses);

          // Format the responses for Airtable
          const formattedResponses = {
            firstName: responses['name']?.firstName || '',
            lastName: responses['name']?.lastName || '',
            email: responses['email']?.email || '',
            phone: responses['phone']?.phone ? `+1${responses['phone'].phone.replace(/\D/g, '')}` : '',
            smsOptIn: ((responses['phone'] as any)?.smsOptOut !== true ? "Yes" : "No") as "Yes" | "No",
            photos: responses['photos'] || [],
            dateTime: new Date().toISOString(),
            gclid: responses['gclid'] || undefined,
            details: Object.entries(responses)
              .filter(([key]) => !['name', 'email', 'phone', 'photos', 'gclid'].includes(key))
              .map(([key, value]) => {
                const step = config.steps.find(s => s.id === key);
                if (!step) return '';
                
                let formattedValue = '';
                if (key === 'weight' && value !== 'unsure') {
                  formattedValue = `${value.value} ${value.unit}`;
                } else if (Array.isArray(value)) {
                  formattedValue = value.join(', ');
                } else {
                  formattedValue = value;
                }
                  
                return `${step.title}: ${formattedValue}\n\n`;
              })
              .join('')
          };

          // Submit to Airtable
          const response = await submitToAirtable(formattedResponses);
          console.log('Successfully submitted form data:', response);
          
          // Show success message
          setSubmissionStatus({
            success: true,
            message: 'Your information has been successfully submitted! We will be in touch shortly.'
          });
          
          // Track form submission
          trackFormSubmission(formattedResponses);
          
          // Track Google Ads conversion
          window.gtag('event', 'conversion', {
            'send_to': 'AW-17043062444/mzG7CP28ycIaEKz94b4_',
            'value': 1.0,
            'currency': 'USD'
          });
          
          // Set completion states
          setIsCompletedLocal(true);
          setIsCompleted(true);
          
          // Call onComplete with the formatted responses
          onComplete(formattedResponses);
        } catch (error) {
          console.error('Failed to submit form data:', error);
          setSubmissionStatus({
            success: false,
            message: 'There was an error submitting your information. Please try again or contact support.'
          });
        }
      } else {
        handleNext();
      }
    } else {
      setShowContactErrors(true);
    }
  };

  // Add a function to get the abbreviated unit
  const getAbbreviatedUnit = (unit: 'grams' | 'ounces' | 'pennyweight') => {
    switch (unit) {
      case 'grams': return 'g';
      case 'ounces': return 'oz';
      case 'pennyweight': return 'dwt';
      default: return unit;
    }
  };

  const handleReviewNav = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setActiveReviewIndex(prev => (prev === 0 ? reviews.length - 1 : prev - 1));
    } else {
      setActiveReviewIndex(prev => (prev === reviews.length - 1 ? 0 : prev + 1));
    }
  };

  switch (step.type) {
    case 'selection':
      const isMultiSelect = step.id === 'metal-type' || 
                          step.id === 'gold-form' || 
                          step.id === 'silver-form' || 
                          step.id === 'platinum-form' || 
                          step.id === 'palladium-form' || 
                          step.id === 'gold-purity' || 
                          step.id === 'silver-purity' || 
                          step.id === 'platinum-purity' || 
                          step.id === 'palladium-purity';
      return (
        <div className="survey-container" key={step.id}>
          <div className="step-header">
            <h2>{getTitle()}</h2>
            {(step.subtitle || isMultiSelect) && (
              <p className="subtitle">
                {step.subtitle}
                {isMultiSelect && (step.subtitle ? ' Select all that apply.' : 'Select all that apply.')}
              </p>
            )}
          </div>
          <div className="options-container">
            {step.options?.map((option) => {
              const isSelected = isMultiSelect
                ? (responses[step.id] as string[] || []).includes(option)
                : responses[step.id] === option;

              return (
                <button
                  key={option}
                  className={`option-button ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleResponse(option)}
                >
                  <span>{option}</span>
                  {isMultiSelect && (
                    <span className="checkbox-icon">
                      {isSelected ? <BsCheckSquare style={{ color: 'white', backgroundColor: '#355c93', borderRadius: '2.9px', boxShadow: 'inset 0 0 0 1px white' }} /> : <BsSquare />}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <button 
            className="next-button"
            onClick={handleNext}
            disabled={isNextDisabled()}
          >
            {currentStep === config.steps.length - 1 ? 'Submit' : 'Next'}
          </button>
        </div>
      );

    case 'text':
      return (
        <div className="survey-container" key={step.id}>
          <div className="step-header">
            <h2>{getTitle()}</h2>
            {step.subtitle && <p className="subtitle">{step.subtitle}</p>}
          </div>
          <textarea
            className="text-input"
            value={responses[step.id] || ''}
            onChange={handleTextInput}
            placeholder="Enter your message here..."
            rows={5}
          />
          <button 
            className="next-button"
            onClick={handleNext}
            disabled={isNextDisabled()}
          >
            {currentStep === config.steps.length - 1 ? 'Submit' : 'Next'}
          </button>
        </div>
      );

      case 'file':
        const client = filestack.init(process.env.REACT_APP_FILESTACK_API_KEY || 'AFbpuXtx3SOtkIi87RA5Az');
      
        const openPicker = () => {
          setIsUploading(true);
          client.picker({
            fromSources: ['local'],
            accept: step.accept || 'image/*',
            maxFiles: step.multiple ? 10 : 1,
            uploadInBackground: false,
            onUploadDone: (res) => {
              handleFileUploadSuccess(res);
              setIsUploading(false);
            },
            onFileUploadFailed: (error) => {
              handleFileUploadError(error);
              setIsUploading(false);
            },
            onClose: () => {
              setIsUploading(false); 
            },
          }).open();
        };
      
        return (
          <div className="survey-container" key={step.id}>
            <div className="step-header">
              <h2>{getTitle()}</h2>
              {step.subtitle && <p>{step.subtitle}</p>}
            </div>
            <div className="file-upload-container">
              <button onClick={openPicker}>Upload Photos</button>
              {isUploading && <p>Uploading files...</p>}
              {responses[step.id] && responses[step.id].length > 0 && (
                <div className="uploaded-files">
                  <p>Uploaded files: {responses[step.id].length}</p>
                </div>
              )}
            </div>
            <QRCodeComponent />
            <button 
              className="next-button"
              onClick={handleNext}
              disabled={isNextDisabled()}
            >
              {currentStep === config.steps.length - 1 ? 'Submit' : 'Next'}
            </button>
          </div>
        );
      

    case 'number':
      return (
        <div className="survey-container" key={step.id}>
          <div className="step-header">
            <h2>{getTitle()}</h2>
            {step.subtitle && <p className="subtitle">{step.subtitle}</p>}
          </div>
          <div className="weight-input-container">
            <div className="weight-input-wrapper">
              <input
                ref={weightInputRef}
                type="number"
                className="number-input"
                value={responses[step.id] === 'unsure' ? '' : (responses[step.id]?.value || '')}
                onChange={handleNumberInput}
                placeholder={`Enter weight`}
                min={step.validation?.min}
                max={step.validation?.max}
                step="0.1"
              />
              <select
                className="unit-select"
                value={weightUnit}
                onChange={handleUnitChange}
                title={weightUnit.charAt(0).toUpperCase() + weightUnit.slice(1)}
                style={{ color: 'transparent' }}
              >
                <option value="grams">Grams (g)</option>
                <option value="ounces">Ounces (oz)</option>
                <option value="pennyweight">Pennyweight (dwt)</option>
              </select>
              <span className="unit-display">{getAbbreviatedUnit(weightUnit)}</span>
            </div>
            <button
              className={`unsure-button ${responses[step.id] === 'unsure' ? 'selected' : ''}`}
              onClick={() => handleResponse('unsure')}
            >
              I'm not sure
            </button>
          </div>
          {error && <p className="error-message">{error}</p>}
          <button 
            className="next-button"
            onClick={handleNext}
            disabled={isNextDisabled()}
          >
            {currentStep === config.steps.length - 1 ? 'Submit' : 'Next'}
          </button>
        </div>
      );

    case 'info':
      return (
        <div className="survey-container" key={step.id}>
          <div className="step-header">
            <h2>{step.title}</h2>
            {step.subtitle && <p className="subtitle">{step.subtitle}</p>}
          </div>
          <div className="info-container" data-step={step.id}>
            {step.id === 'customer-reviews' ? (
              <div className="reviews-wrapper">
                <button className="review-nav prev" onClick={() => handleReviewNav('prev')}>
                </button>
                <div 
                  className="reviews-track"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {reviews.map((review, index) => {
                    // Calculate the position class based on the difference from active index
                    const diff = (index - activeReviewIndex + reviews.length) % reviews.length;
                    let positionClass = '';
                    
                    // For mobile screens, we only show the active review
                    const isMobile = window.innerWidth <= 768;
                    
                    if (isMobile) {
                      positionClass = diff === 0 ? 'active' : '';
                    } else {
                      // Desktop view - show active and adjacent reviews
                      if (diff === 0) {
                        positionClass = 'active';
                      } else if (diff === 1 || diff === -reviews.length + 1) {
                        positionClass = 'next-1';
                      } else if (diff === 2 || diff === -reviews.length + 2) {
                        positionClass = 'next-2';
                      } else if (diff === -1 || diff === reviews.length - 1) {
                        positionClass = 'prev-1';
                      } else if (diff === -2 || diff === reviews.length - 2) {
                        positionClass = 'prev-2';
                      }
                    }

                    // Only render the review if it has a position class
                    if (positionClass) {
                      return (
                        <div 
                          key={index}
                          className={`review-card ${positionClass} ${isSwiping ? 'swiping' : ''}`}
                          onClick={() => setActiveReviewIndex(index)}
                        >
                          <div className="stars">★★★★★</div>
                          <p className="review-text">{review.text}</p>
                          <p className="reviewer">{review.reviewer}</p>
                          <p className="review-date">{review.date}</p>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
                <button className="review-nav next" onClick={() => handleReviewNav('next')}>
                </button>
                <div className="review-dots">
                  {reviews.map((_, index) => (
                    <div
                      key={index}
                      className={`review-dot ${index === activeReviewIndex ? 'active' : ''}`}
                      onClick={() => setActiveReviewIndex(index)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <img src={step.image} alt={step.title} className="info-image" />
            )}
          </div>
          <button 
            className="next-button" 
            onClick={handleNext}
            disabled={isNextDisabled()}
          >
            Continue
          </button>
        </div>
      );

    case 'review':
      return (
        <div className="survey-container" key={step.id}>
          <div className="step-header">
            <h2>{step.title}</h2>
            {step.subtitle && <p className="subtitle">{step.subtitle}</p>}
          </div>
          <div className="review-container">
            {Object.entries(responses).map(([key, value]) => {
              const step = config.steps.find(s => s.id === key);
              if (!step || key === 'gclid') return null;
              
              let formattedValue = '';
              if (key === 'weight' && value !== 'unsure') {
                formattedValue = `${value.value} ${value.unit}`;
              } else if (Array.isArray(value)) {
                formattedValue = value.join(', ');
              } else if (typeof value === 'object' && value !== null) {
                if (key === 'name') {
                  formattedValue = `${value.firstName} ${value.lastName}`;
                } else if (key === 'email') {
                  formattedValue = value.email;
                } else if (key === 'phone') {
                  formattedValue = value.phone;
                }
              } else {
                formattedValue = value;
              }
              
              return (
                <div key={key} className="review-item">
                  <h3>{step.title}</h3>
                  <p>{formattedValue}</p>
                </div>
              );
            })}
          </div>
          <button 
            className="next-button" 
            onClick={handleNext}
            disabled={isNextDisabled()}
          >
            Continue
          </button>
        </div>
      );

    case 'contact':
      return (
        <div className="survey-container" key={step.id}>
          <div className="step-header">
            <h2>{getTitle()}</h2>
            {step.subtitle && <p className="subtitle">{step.subtitle}</p>}
          </div>
          <div className="contact-form">
            {step.fields?.includes('firstName') && step.fields?.includes('lastName') && (
              <div className="name-fields">
                <div className="input-group">
                  <input
                    ref={nameInputRef}
                    type="text"
                    className="text-input"
                    value={(responses[step.id] as any)?.firstName || ''}
                    onChange={(e) => {
                      handleResponse({ ...responses[step.id] as any, firstName: e.target.value });
                      if (showContactErrors && (!e.target.value || e.target.value.trim() === '')) {
                        setError('First name is required');
                      } else {
                        setError('');
                      }
                    }}
                    placeholder="First Name *"
                    required
                  />
                  {showContactErrors && (!(responses[step.id] as any)?.firstName || (responses[step.id] as any)?.firstName.trim() === '') && (
                    <p className="error-message" style={{ color: '#ff0000', fontSize: '0.875rem', marginTop: '0.25rem' }}>First name is required</p>
                  )}
                </div>
                <div className="input-group">
                  <input
                    type="text"
                    className="text-input"
                    value={(responses[step.id] as any)?.lastName || ''}
                    onChange={(e) => {
                      handleResponse({ ...responses[step.id] as any, lastName: e.target.value });
                      if (showContactErrors && (!e.target.value || e.target.value.trim() === '')) {
                        setError('Last name is required');
                      } else {
                        setError('');
                      }
                    }}
                    placeholder="Last Name *"
                    required
                  />
                  {showContactErrors && (!(responses[step.id] as any)?.lastName || (responses[step.id] as any)?.lastName.trim() === '') && (
                    <p className="error-message" style={{ color: '#ff0000', fontSize: '0.875rem', marginTop: '0.25rem' }}>Last name is required</p>
                  )}
                </div>
              </div>
            )}
            {step.fields?.includes('email') && (
              <div className="input-group">
                <input
                  type="email"
                  className="text-input"
                  value={(responses[step.id] as any)?.email || ''}
                  onChange={(e) => {
                    const email = e.target.value;
                    handleResponse({ ...responses[step.id] as any, email });
                    if (showContactErrors) {
                      if (!email || email.trim() === '') {
                        setEmailError('Email is required');
                      } else if (!validateEmail(email)) {
                        setEmailError('Please enter a valid email address');
                      } else {
                        setEmailError('');
                      }
                    }
                  }}
                  placeholder="Email Address *"
                  required
                />
                {showContactErrors && emailError && (
                  <p className="error-message" style={{ color: '#ff0000', fontSize: '0.875rem', marginTop: '0.25rem' }}>{emailError}</p>
                )}
              </div>
            )}
            {step.fields?.includes('phone') && (
              <div className="input-group">
                <input
                  type="tel"
                  className="text-input"
                  value={(responses[step.id] as any)?.phone || ''}
                  onChange={(e) => {
                    const phone = e.target.value;
                    handleResponse({ ...responses[step.id] as any, phone });
                    // Only validate if user has tried to proceed
                    if (showContactErrors && phone && !validatePhone(phone)) {
                      setError('Please enter a valid 10-digit phone number');
                    } else {
                      setError('');
                    }
                  }}
                  placeholder="Phone Number *"
                  required
                />
                {/* Only show error if user has tried to proceed AND entered invalid number */}
                {showContactErrors && error && error.includes('phone') && (responses[step.id] as any)?.phone && !validatePhone((responses[step.id] as any)?.phone) && (
                  <p className="error-message" style={{ color: '#ff0000', fontSize: '0.875rem', marginTop: '0.25rem' }}>{error}</p>
                )}
              </div>
            )}
            {step.fields?.includes('smsOptIn') && (
              <div className="input-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={(responses[step.id] as any)?.smsOptOut || false}
                    onChange={(e) => handleResponse({ 
                      ...responses[step.id] as any, 
                      smsOptOut: e.target.checked 
                    })}
                    disabled={!validatePhone((responses[step.id] as any)?.phone || '')}
                  />
                  <span className={!validatePhone((responses[step.id] as any)?.phone || '') ? 'disabled' : ''}>
                    I would not like to receive SMS updates on my submission
                  </span>
                </label>
                {showContactErrors && (responses[step.id] as any)?.phone && !validatePhone((responses[step.id] as any)?.phone) && (
                  <p className="hint-message" style={{ color: '#ff0000', fontSize: '0.875rem', marginTop: '0.25rem' }}>Please enter a valid phone number to enable SMS opt-in</p>
                )}
              </div>
            )}
          </div>
          <button 
            className="next-button"
            disabled={!hasAnyInput()}
            style={{ opacity: hasAnyInput() ? 1 : 0.5 }}
            onClick={handleContactNext}
          >
            {currentStep === config.steps.length - 1 ? 'Submit' : 'Next'}
          </button>
        </div>
      );

    default:
      return <div>Unsupported step type: {step.type}</div>;
  }
};

const Survey: React.FC<SurveyProps> = ({ currentStep, setCurrentStep, history, setHistory, setIsCompleted, responses, setResponses, QRCodeComponent, onComplete }) => {
  const [error, setError] = useState<string>('');
  const [isCompletedLocal, setIsCompletedLocal] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [showContactErrors, setShowContactErrors] = useState(false);
  const [weightUnit, setWeightUnit] = useState<'grams' | 'ounces' | 'pennyweight'>('grams');
  const nameInputRef = React.useRef<HTMLInputElement | null>(null);
  const weightInputRef = React.useRef<HTMLInputElement | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Add Google Ads conversion tracking function
  const gtag_report_conversion = () => {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17043062444/mzG7CP28ycIaEKz94b4_',
      'value': 1.0,
      'currency': 'USD'
    });
    return false;
  };

  const config = rawConfig as SurveyConfig;
  const step = config.steps[currentStep];

  // Capture gclid from URL when component mounts
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gclid = urlParams.get('gclid');
    if (gclid) {
      setResponses(prev => ({
        ...prev,
        gclid
      }));
    }
  }, []);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Check if the number has 10 digits (US format)
    return digits.length === 10;
  };

  // Get all steps that should be visible based on current responses
  const getVisibleSteps = () => {
    return config.steps.filter(s => {
      if (!s.showWhen) return true;
      const [dependentStepId, requiredValue] = Object.entries(s.showWhen)[0];
      // Check if the required metal type is in the array of selected metals
      if (dependentStepId === 'metal-type') {
        const selectedMetals = responses[dependentStepId] as string[] || [];
        return selectedMetals.includes(requiredValue as string);
      }
      return responses[dependentStepId] === requiredValue;
    });
  };

  // Calculate progress based on visible steps
  const calculateProgress = () => {
    const visibleSteps = getVisibleSteps();
    const currentPosition = visibleSteps.findIndex(s => s.id === step.id) + 1;
    return (currentPosition / visibleSteps.length) * 100;
  };

  // Track survey start when component mounts
  React.useEffect(() => {
    if (currentStep === 0) {
      trackSurveyStart();
    }
  }, []);

  const handleNext = async () => {
    // Only track step completion if it's not the final step
    if (currentStep !== config.steps.length - 1) {
      trackStepComplete(step.id, step.title, responses);
    }

    if (currentStep === config.steps.length - 1) {
      // Validate contact information before submitting
      const contactInfo = responses['contact'] as { firstName?: string; lastName?: string; email?: string; phone?: string } | undefined;
      const email = contactInfo?.email || '';
      const phone = contactInfo?.phone || '';
      
      // Set showContactErrors to true to display any validation errors
      setShowContactErrors(true);
      
      // Check if any required fields are missing or invalid
      if (!contactInfo?.firstName || !contactInfo?.lastName || !email || !validateEmail(email) || !phone || !validatePhone(phone)) {
        return;
      }

      setIsCompletedLocal(true);
      setIsCompleted(true);
      
      // Format the responses according to the new schema
      const formattedResponses = {
        firstName: responses['name']?.firstName || '',
        lastName: responses['name']?.lastName || '',
        email: responses['email']?.email || '',
        phone: responses['phone']?.phone ? `+1${responses['phone'].phone.replace(/\D/g, '')}` : '',
        smsOptIn: ((responses['phone'] as any)?.smsOptOut !== true ? "Yes" : "No") as "Yes" | "No",
        photos: responses['photos'] || [],
        dateTime: new Date().toISOString(),
        gclid: responses['gclid'] || undefined,
        details: Object.entries(responses)
          .filter(([key]) => !['name', 'email', 'phone', 'photos', 'gclid'].includes(key))
          .map(([key, value]) => {
            const step = config.steps.find(s => s.id === key);
            if (!step) return '';
            
            let formattedValue = '';
            if (key === 'weight' && value !== 'unsure') {
              formattedValue = `${value.value} ${value.unit}`;
            } else if (Array.isArray(value)) {
              formattedValue = value.join(', ');
            } else {
              formattedValue = value;
            }
              
            return `${step.title}: ${formattedValue}\n\n`;
          })
          .join('')
      };

      try {
        // Submit to webhook
        const response = await submitToAirtable(formattedResponses);
        console.log('Successfully submitted form data:', response);
        
        // Show success message to user
        setSubmissionStatus({
          success: true,
          message: 'Your information has been successfully submitted! We will be in touch shortly.'
        });
        
        // Track form submission in Mixpanel
        trackFormSubmission(formattedResponses);

        // Track Google Ads conversion
        gtag_report_conversion();
      } catch (error) {
        console.error('Failed to submit form data:', error);
        setSubmissionStatus({
          success: false,
          message: 'There was an error submitting your information. Please try again or contact support.'
        });
        return;
      }

      onComplete(formattedResponses);
      return;
    }

    let nextStep = currentStep + 1;
    while (nextStep < config.steps.length) {
      const nextStepData = config.steps[nextStep];

      if (!nextStepData.showWhen) break;
      
      const [dependentStepId, requiredValue] = Object.entries(nextStepData.showWhen)[0];
      if (dependentStepId === 'metal-type') {
        const selectedMetals = responses[dependentStepId] as string[] || [];
        if (selectedMetals.includes(requiredValue as string)) break;
      } else if (responses[dependentStepId] === requiredValue) {
        break;
      }
      
      nextStep++;
    }
    
    if (nextStep < config.steps.length) {
      setHistory(prev => [...prev, nextStep]);
      setCurrentStep(nextStep);
      setError('');
    }
  };

  const handleResponse = (value: any) => {
    if (step.id === 'metal-type' || 
        step.id === 'gold-form' || 
        step.id === 'silver-form' || 
        step.id === 'platinum-form' || 
        step.id === 'palladium-form' || 
        step.id === 'gold-purity' || 
        step.id === 'silver-purity' || 
        step.id === 'platinum-purity' || 
        step.id === 'palladium-purity') {
      // Handle multiple selections for metal type, form, and purity questions
      setResponses(prev => {
        const currentSelections = prev[step.id] as string[] || [];
        const newSelections = currentSelections.includes(value)
          ? currentSelections.filter(item => item !== value)
          : [...currentSelections, value];
        
        // Track metal selection if it's the metal-type step
        if (step.id === 'metal-type') {
          trackMetalSelection(newSelections);
        }
        
        return {
          ...prev,
          [step.id]: newSelections
        };
      });
    } else if (step.type === 'number' && value === 'unsure') {
      // Handle "I'm not sure" response for weight
      setResponses(prev => ({
        ...prev,
        [step.id]: 'unsure'
      }));
      trackWeightEntry('unsure');
    } else {
      setResponses(prev => ({
        ...prev,
        [step.id]: value
      }));
      
      // Track weight entry for number inputs
      if (step.type === 'number') {
        trackWeightEntry(value);
      }
    }
    setError('');
  };

  const handleNumberInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear unsure state when user starts typing
    if (responses[step.id] === 'unsure') {
      setResponses(prev => ({
        ...prev,
        [step.id]: 'unsure'
      }));
      return;
    }

    const value = parseFloat(e.target.value);
    
    if (step.validation?.min !== undefined && step.validation?.max !== undefined) {
      if (value < step.validation.min || value > step.validation.max) {
        setError(step.validation.message || `Please enter a valid weight between ${step.validation.min} and ${step.validation.max}`);
        return;
      }
    }
    
    // Store both the weight and unit
    setResponses(prev => ({
      ...prev,
      [step.id]: {
        value,
        unit: weightUnit
      }
    }));
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newUnit = e.target.value as 'grams' | 'ounces' | 'pennyweight';
    setWeightUnit(newUnit);
    
    // Update the unit in the stored response if there is a value
    if (responses[step.id] && responses[step.id] !== 'unsure') {
      setResponses(prev => ({
        ...prev,
        [step.id]: {
          ...prev[step.id],
          unit: newUnit
        }
      }));
    }
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleResponse(e.target.value);
  };

  const isNextDisabled = () => {
    if (error) return true;
    
    switch (step.type) {
      case 'selection':
        const isMultiSelect = step.id === 'metal-type' || 
                            step.id === 'gold-form' || 
                            step.id === 'silver-form' || 
                            step.id === 'platinum-form' || 
                            step.id === 'palladium-form' || 
                            step.id === 'gold-purity' || 
                            step.id === 'silver-purity' || 
                            step.id === 'platinum-purity' || 
                            step.id === 'palladium-purity';
        return isMultiSelect ? (responses[step.id] as string[] || []).length === 0 : !responses[step.id];
      case 'file':
        // File upload is optional
        return false;
      case 'number':
        return responses[step.id] === undefined || responses[step.id] === '';
      case 'text':
        // Text input is optional
        return false;
      case 'contact':
        // Don't disable the button for contact form
        return false;
      case 'info':
        return false;
      case 'review':
        return false;
      default:
        return false;
    }
  };

  React.useEffect(() => {
    if (!step) return;

    const visibleSteps = getVisibleSteps();
    const visibleStepNumber = visibleSteps.findIndex(s => s.id === step.id) + 1;

    if (step.showWhen) {
      const [dependentStepId, requiredValue] = Object.entries(step.showWhen)[0];
      if (dependentStepId === 'metal-type') {
        const selectedMetals = responses[dependentStepId] as string[] || [];
        if (!selectedMetals.includes(requiredValue as string)) {
          // Remove tracking from here, only advance
          handleNext();
        }
      } else if (responses[dependentStepId] !== requiredValue) {
        // Remove tracking from here, only advance
        handleNext();
      }
    }
  }, [currentStep, responses]);

  React.useEffect(() => {
    if (step?.type === 'contact') {
      nameInputRef.current?.focus();
    } else if (step?.type === 'number') {
      weightInputRef.current?.focus();
    }
  }, [step]);

  if (isCompletedLocal) {
    return (
      <div className="survey-container thank-you">
        <h2>Thank You!</h2>
        {submissionStatus && (
          <div className={`submission-status ${submissionStatus.success ? 'success' : 'error'}`}>
            {submissionStatus.message}
          </div>
        )}
        <p className="subtitle">
          We've received your information and will be in touch shortly. One of our representatives will review your submission and contact you to discuss the next steps.
        </p>
        <p className="contact-info">
          If you have any immediate questions, please don't hesitate to reach out:
          <br />
          Email: info@abemor.com
          <br />
          Phone: (212) 921-4220
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="progress-container" style={{ opacity: currentStep === 0 ? 0 : 1 }}>
        <div className="progress-bar">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${calculateProgress()}%` }} 
          />
        </div>
      </div>
      <SurveyStepComponent
        step={step}
        responses={responses}
        setResponses={setResponses}
        handleNext={handleNext}
        handleResponse={handleResponse}
        handleNumberInput={handleNumberInput}
        handleTextInput={handleTextInput}
        error={error}
        setError={setError}
        emailError={emailError}
        setEmailError={setEmailError}
        nameInputRef={nameInputRef}
        weightInputRef={weightInputRef}
        isNextDisabled={isNextDisabled}
        currentStep={currentStep}
        config={config}
        QRCodeComponent={QRCodeComponent}
        validateEmail={validateEmail}
        validatePhone={validatePhone}
        showContactErrors={showContactErrors}
        setShowContactErrors={setShowContactErrors}
        setIsCompletedLocal={setIsCompletedLocal}
        setIsCompleted={setIsCompleted}
        onComplete={onComplete}
        submissionStatus={submissionStatus}
        setSubmissionStatus={setSubmissionStatus}
        weightUnit={weightUnit}
        handleUnitChange={handleUnitChange}
      />
    </div>
  );
};

export default Survey; 